import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const BusinessSubCategory = () => {
  const [businessCategories, setBusinessCategories] = useState([]);
  const [businessSubCategories, setBusinessSubCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({ category: '', name: '' });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);

  useEffect(() => {
    fetchBusinessCategories();
  }, []);

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchBusinessSubCategories(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  // Fetch Categories for dropdown
  const fetchBusinessCategories = async () => {
    try {
      const res = await axios.get('/admin/api/businessCategory');
      setBusinessCategories(res.data.businessCategories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  // Fetch Subcategories list with pagination and search
  const fetchBusinessSubCategories = async (page = 1, search = '') => {
    try {
      const res = await axios.get(
        `/admin/api/businessSubCategory?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
      );
      const { businessSubCategories = [], totalCount = 0 } = res.data;
      setBusinessSubCategories(businessSubCategories);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch SubCategory:', err);
    }
  };

  // Save new or updated SubCategory
  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      category: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/admin/api/businessSubCategory/${editingId}`, formValues);
        toastSuccess('SubCategory updated successfully!');
      } else {
        await axios.post('/admin/api/businessSubCategory', formValues);
        toastSuccess('SubCategory added successfully!');
      }
      setCurrentPage(1);
      fetchBusinessSubCategories(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save SubCategory:', err);
      toastError('Failed to save SubCategory');
    }
  };

  // Open edit modal with pre-filled values
  const handleEdit = (subCategory) => {
    setFormValues({
      name: subCategory.name || '',
      category: subCategory.category?._id || '',
    });
    setEditingId(subCategory._id);
    setShowModal(true);
  };

  // Delete subcategory by id
  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this subcategory?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/businessSubCategory/${id}`);
        toastSuccess('SubCategory deleted successfully!');
        setCurrentPage(1);
        fetchBusinessSubCategories(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete SubCategory:', err);
        toastError('Failed to delete SubCategory');
      }
    }
  };

  // Close modal and reset form
  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ category: '', name: '' });
    setEditingId(null);
    setErrors({});
  };

  // Prepare dropdown options for categories
  const categoryOptions = businessCategories.map(cat => ({
    label: cat.name,
    value: cat._id,
  }));

  // Define modal form fields including category dropdown
  const fields = [
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: categoryOptions,
    },
    { name: 'name', label: 'SubCategory', placeholder: 'Enter SubCategory Name' },
  ];

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> SubCategory</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search SubCategory (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">SubCategory List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New SubCategory
          </button>
        </div>
      <div className="table-responsive">
        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2 w-50">SubCategory Name</th>
                <th className="py-1 px-2 w-30">Category</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {businessSubCategories.length > 0 ? (
                businessSubCategories.map((subCat, idx) => (
                  <tr key={subCat._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">{subCat.name}</td>
                    <td className="py-1 px-2">{subCat.category?.name || '-'}</td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(subCat)}
                      >
                        <FaEdit size={14} />
                      </button>
                     {/*  <button
                                             className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                                             onClick={() => handleDelete(subCat._id)}
                                             title="Delete"
                                           >
                                             <FaTrash size={14} />
                                           </button> */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-2">
                    No SubCategory found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>
              {/* Pagination numbers with ellipsis */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                )
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    acc.push('ellipsis');
                  }
                  acc.push(page);
                  return acc;
                }, [])
                .map((page, idx) =>
                  page === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="px-1">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`btn btn-sm page-btn${currentPage === page ? ' active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit SubCategory' : 'Add SubCategory'}
        fields={fields}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={['name']}
      />
    </div>
  );
};

export default BusinessSubCategory;
